import { useState, useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageCard, MessageDetail, MessageCompose } from '../components/inbox'
import { WorkOrderCompose } from '../components/work-orders'
import type { BrainMessage, BrainAgent } from '../types/brain'
import { fetchInboxMessages, ackBrainMessage, fetchBrainAgents, type InboxSource } from '../api/brain'
import { ErrorState } from '../components/ui'
import { usePageTitle } from '../hooks/usePageTitle'

type FilterTab = 'all' | 'work_orders' | 'notifications' | 'status_updates' | 'sent'

const TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'work_orders', label: 'Work Orders' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'status_updates', label: 'Status Updates' },
  { key: 'sent', label: 'Sent' },
]

const REFETCH_INTERVAL_MS = 60_000

export function Inbox() {
  usePageTitle('Inbox')
  const queryClient = useQueryClient()
  const [selectedMessage, setSelectedMessage] = useState<BrainMessage | null>(null)
  const [showCompose, setShowCompose] = useState(false)
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [ackError, setAckError] = useState<string | null>(null)
  const [composePrefill, setComposePrefill] = useState<Partial<{
    to: string
    type: string
    subject: string
    body: string
  }> | null>(null)
  const [showConvertToWorkOrder, setShowConvertToWorkOrder] = useState(false)
  const [workOrderPrefill, setWorkOrderPrefill] = useState<Partial<{
    subject: string
    instructions: string
    targetBrain: string
  }> | null>(null)

  // Inbox query (received messages)
  const {
    data: inboxData,
    isLoading: inboxLoading,
    error: inboxError,
    dataUpdatedAt,
    refetch: refetchInbox,
  } = useQuery({
    queryKey: ['inbox'],
    queryFn: () => fetchInboxMessages(),
    refetchInterval: REFETCH_INTERVAL_MS,
    retry: 2,
  })

  // Sent messages query — fetches include_sent=true then shows only messages from decisive
  const {
    data: allWithSentData,
    isLoading: sentLoading,
    refetch: refetchSent,
  } = useQuery({
    queryKey: ['inbox-sent'],
    queryFn: () => fetchInboxMessages({ includeSent: true }),
    refetchInterval: REFETCH_INTERVAL_MS,
    retry: 2,
    enabled: activeTab === 'sent',
  })

  // Agents query for Compose dropdown
  const { data: agentsData } = useQuery({
    queryKey: ['brain-agents'],
    queryFn: fetchBrainAgents,
    staleTime: 5 * 60_000,
  })

  const messages: BrainMessage[] = inboxData?.messages ?? []
  const inboxIds = useMemo(() => new Set(messages.map((m) => m.id)), [messages])

  // Sent = allWithSent minus received
  const sentMessages: BrainMessage[] = useMemo(() => {
    const all = allWithSentData?.messages ?? []
    return all.filter((m) => !inboxIds.has(m.id))
  }, [allWithSentData, inboxIds])

  const agents: BrainAgent[] = agentsData?.agents ?? []

  const isLoading = inboxLoading || (activeTab === 'sent' && sentLoading)
  const error = inboxError instanceof Error ? inboxError.message : inboxError ? String(inboxError) : null
  const lastRefreshed = dataUpdatedAt > 0 ? new Date(dataUpdatedAt) : null

  const handleRefresh = useCallback(() => {
    void refetchInbox()
    if (activeTab === 'sent') void refetchSent()
  }, [refetchInbox, refetchSent, activeTab])

  const handleAck = useCallback(
    async (id: string) => {
      // Optimistic: remove from cache immediately
      queryClient.setQueryData<{ messages: BrainMessage[]; source: InboxSource; cached: boolean }>(
        ['inbox'],
        (prev) => {
          if (!prev) return prev
          return { ...prev, messages: prev.messages.filter((m) => m.id !== id) }
        },
      )
      if (selectedMessage?.id === id) setSelectedMessage(null)
      try {
        await ackBrainMessage(id)
      } catch (err) {
        console.error('Ack error:', err)
        setAckError('Failed to acknowledge message — refreshing inbox.')
        void refetchInbox()
        setTimeout(() => setAckError(null), 5000)
      }
    },
    [selectedMessage, refetchInbox, queryClient],
  )

  const handleMessageClick = useCallback((msg: BrainMessage) => {
    setSelectedMessage((prev) => (prev?.id === msg.id ? null : msg))
  }, [])

  // Filter messages by active tab
  const filteredMessages = useMemo(() => {
    if (activeTab === 'sent') return []
    return messages.filter((m) => {
      switch (activeTab) {
        case 'work_orders': return m.type === 'work_order_update'
        case 'notifications': return m.type === 'notification'
        case 'status_updates': return m.type === 'status_update'
        default: return true
      }
    })
  }, [messages, activeTab])

  const activeMessages = activeTab === 'sent' ? sentMessages : filteredMessages

  const tabCounts = useMemo(() => ({
    all: messages.length,
    work_orders: messages.filter((m) => m.type === 'work_order_update').length,
    notifications: messages.filter((m) => m.type === 'notification').length,
    status_updates: messages.filter((m) => m.type === 'status_update').length,
    sent: sentMessages.length,
  }), [messages, sentMessages])

  const brainOptions = useMemo(() => (
    agents.map((agent) => ({
      value: agent.agentId,
      label: agent.agentId,
    }))
  ), [agents])

  const handleReply = useCallback((message: BrainMessage) => {
    setComposePrefill({
      to: message.from,
      type: message.type === 'request' ? 'notification' : message.type,
      subject: `Re: ${message.subject}`,
      body: typeof message.body === 'string' ? message.body : JSON.stringify(message.body, null, 2),
    })
    setShowCompose(true)
  }, [])

  const handleConvertToWorkOrder = useCallback((message: BrainMessage) => {
    setWorkOrderPrefill({
      subject: `Follow-up: ${message.subject}`,
      instructions: typeof message.body === 'string' ? message.body : JSON.stringify(message.body, null, 2),
      targetBrain: message.from,
    })
    setShowConvertToWorkOrder(true)
  }, [])

  return (
    <div className="min-h-screen bg-swiss-white">
      {/* Page header */}
      <div className="border-b border-swiss-gray-200 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-[22px] font-semibold tracking-tight">Inbox</h1>
          {messages.length > 0 && (
            <span className="text-[11px] font-medium uppercase tracking-widest bg-swiss-black text-swiss-white px-2 py-0.5">
              {messages.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-[11px] font-swiss-mono text-swiss-gray-500">
              {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-3 py-1.5 text-[12px] font-medium border border-swiss-gray-200 text-swiss-black cursor-pointer hover:border-swiss-black transition-colors duration-100 disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowCompose(true)}
            className="px-3 py-1.5 text-[12px] font-medium bg-swiss-black text-swiss-white cursor-pointer hover:bg-swiss-gray-800 transition-colors duration-100"
          >
            Compose
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div role="tablist" aria-label="Inbox filters" className="border-b border-swiss-gray-200 px-8 flex gap-0">
        {TABS.map((tab) => {
          const count = tabCounts[tab.key]
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-[12px] font-medium uppercase tracking-widest border-b-2 transition-colors duration-100 cursor-pointer ${
                isActive
                  ? 'border-b-swiss-black text-swiss-black'
                  : 'border-b-transparent text-swiss-gray-500 hover:text-swiss-black'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className="ml-1.5 text-[10px] font-swiss-mono">{count}</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Content */}
      <div>
        {/* Load error */}
        {error && (
          <div className="px-8 py-6">
            <ErrorState message="Failed to load inbox" onRetry={handleRefresh} />
          </div>
        )}

        {/* ACK error */}
        {ackError && (
          <div className="px-8 py-4 text-[13px] text-swiss-red border-b border-swiss-gray-200">
            {ackError}
          </div>
        )}

        {inboxData && inboxData.source !== 'admp' && (
          <div className="px-8 py-3 text-[13px] bg-amber-50 border-b border-amber-200 text-amber-950">
            ADMP not configured locally — showing brain-server fallback data.
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="px-8 py-6 text-[13px] text-swiss-gray-500">
            Loading inbox...
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && activeMessages.length === 0 && (
          <div className="px-8 py-12 text-center">
            <p className="text-[13px] text-swiss-gray-500">
              {activeTab === 'sent' ? 'No sent messages yet.' : 'Inbox is clear — no messages for decisive.'}
            </p>
          </div>
        )}

        {/* Message list */}
        {!isLoading && activeMessages.length > 0 && (
          <div>
            {activeMessages.map((msg) => (
              <div key={msg.id}>
                <MessageCard
                  message={msg}
                  onAck={handleAck}
                  onClick={handleMessageClick}
                  isSelected={selectedMessage?.id === msg.id}
                  isSent={activeTab === 'sent'}
                />
                {selectedMessage?.id === msg.id && (
                  <MessageDetail
                    message={msg}
                    onClose={() => setSelectedMessage(null)}
                    onAck={handleAck}
                    onReply={handleReply}
                    onConvertToWorkOrder={handleConvertToWorkOrder}
                    isSent={activeTab === 'sent'}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <MessageCompose
          onClose={() => { setShowCompose(false); setComposePrefill(null) }}
          onSent={() => {
            setComposePrefill(null)
            void refetchInbox()
          }}
          brainOptions={agents}
          initialTo={composePrefill?.to}
          initialType={composePrefill?.type}
          initialSubject={composePrefill?.subject}
          initialBody={composePrefill?.body}
        />
      )}

      {showConvertToWorkOrder && (
        <WorkOrderCompose
          onClose={() => setShowConvertToWorkOrder(false)}
          onCreated={() => {
            setShowConvertToWorkOrder(false)
            setWorkOrderPrefill(null)
          }}
          brainOptions={brainOptions}
          initialSubject={workOrderPrefill?.subject}
          initialInstructions={workOrderPrefill?.instructions}
          initialTargetBrain={workOrderPrefill?.targetBrain}
        />
      )}
    </div>
  )
}
